/* Collection grid: tapping a colour swatch previews that colour in place.
 *
 * Impulse already swaps the tile image on swatch HOVER, which does nothing on
 * a phone — there, tapping a swatch just navigated to the product page, so
 * comparing colours meant a round trip per colour.
 *
 * Touch only. On a mouse, hover preview plus click-to-open is the right
 * behaviour and is left exactly as Impulse ships it.
 *
 * Reuses Impulse's own swap layer rather than adding one: each swatch already
 * names a .grid-product__color-image--<id> overlay and carries the image to
 * put in it. This just drives that on tap instead of on hover, and makes it
 * stick rather than reverting when the finger lifts.
 */
(function () {
  'use strict';

  var SWATCH = '.color-swatch--with-image';

  // Tap-to-preview is touch-only: on a mouse, Impulse's hover preview plus
  // click-to-open is already right. The scroll hint below is not gated —
  // knowing a row scrolls is useful with a trackpad too.
  var IS_TOUCH = window.matchMedia && window.matchMedia('(hover: none)').matches;

  function tileOf(el) {
    return el.closest('.grid-product') || el.closest('.grid__item');
  }

  /* Drives the hairline scroll indicator under a swatch row: shows it only
   * where the row actually overflows, and keeps its thumb in step.
   *
   * Measuring scrollWidth forces layout, and a collection page can carry a
   * hundred of these — so rows are measured lazily as they scroll into view
   * and then unobserved, rather than all at once on load.
   *
   * Runs on every pointer type: knowing a row scrolls is as useful with a
   * trackpad as with a finger.
   */
  function initScrollHints() {
    var scrollers = document.querySelectorAll('.sm-swatch-scroller');
    if (!scrollers.length || !window.IntersectionObserver) return;

    function measure(scroller) {
      var row = scroller.querySelector('.grid-product__colors');
      var bar = scroller.querySelector('.sm-swatch-bar');
      if (!row || !bar) return;

      var overflow = row.scrollWidth - row.clientWidth;
      if (overflow <= 2) {
        scroller.classList.remove('sm-scrollable');
        return;
      }
      scroller.classList.add('sm-scrollable');

      // Thumb width is the visible fraction of the row; position is how far
      // through the remaining scroll we are. Floored at 18% so a row with a
      // great many colours still has a thumb you can see.
      var frac = Math.max(row.clientWidth / row.scrollWidth, 0.18);
      var progress = row.scrollLeft / overflow;
      bar.style.setProperty('--sm-thumb-w', (frac * 100) + '%');
      bar.style.setProperty('--sm-thumb-x', (progress * (1 - frac) * 100) + '%');
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var scroller = entry.target;
        io.unobserve(scroller);
        measure(scroller);

        // Keep the thumb in step with the row.
        var row = scroller.querySelector('.grid-product__colors');
        if (!row) return;
        var ticking = false;
        row.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            measure(scroller);
          });
        }, { passive: true });
      });
    }, { rootMargin: '200px 0px' });

    scrollers.forEach(function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollHints);
  } else {
    initScrollHints();
  }

  document.addEventListener('click', function (evt) {
    if (!IS_TOUCH) return;
    var swatch = evt.target.closest(SWATCH);
    if (!swatch) return;

    var tile = tileOf(swatch);
    if (!tile) return;

    var image = swatch.getAttribute('data-variant-image');
    if (!image) return; // nothing to preview - let the link do its job

    // One layer per tile, created on first tap.
    //
    // Impulse pre-renders one of these per colour for its hover preview,
    // which on a colour-expanded grid is over a thousand positioned divs on
    // a phone. Those stay desktop-only; here the tile gets a single layer
    // the first time it's needed and reuses it for every colour after.
    var mask = tile.querySelector('.grid-product__image-mask') || tile;
    var layer = mask.querySelector('.sm-swatch-preview');
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'sm-swatch-preview';
      mask.appendChild(layer);
    }

    // Only swallow the navigation once there's definitely something to show.
    evt.preventDefault();

    layer.style.backgroundImage = 'url(' + image + ')';
    layer.classList.add('is-active');

    tile.querySelectorAll(SWATCH).forEach(function (el) {
      el.classList.toggle('sm-is-selected', el === swatch);
    });

    // Point the tile at the colour being previewed, so opening the product
    // lands on it rather than on whatever the tile started as.
    var url = swatch.getAttribute('data-variant-url');
    if (!url) return;
    tile.querySelectorAll('a.grid-product__link').forEach(function (link) {
      link.setAttribute('href', url);
    });
  });
})();
